import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { SendIcon, XCircleIcon, SaveIcon, GripHorizontalIcon, EyeOffIcon, EyeIcon, Edit3Icon, CheckCircle2Icon, XIcon, RefreshCwIcon, AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, HelpCircleIcon } from 'lucide-react';
import { DocumentSidebar } from './DocumentSidebar';
import { DocumentDataPanel } from './DocumentDataPanel';
import { ImageViewerPanel } from './ImageViewerPanel';
import { LineItemsPanel } from './LineItemsPanel';
import { mockInvoices, mockDocuments, getDocumentDetails, InvoiceDocument } from './documentReviewTypes';
import { InvoiceDocument as ExtendedInvoiceDocument, LineItem as ExtendedLineItem } from '../types/invoice';
import { validateInvoice } from '../utils/invoiceValidation';
const STORAGE_KEY_DOCUMENT_ID = 'current_document_id';
// Adapt documentReviewTypes document to the extended type used by ImageViewerPanel and LineItemsPanel
function adaptDocument(doc: InvoiceDocument): ExtendedInvoiceDocument {
  return {
    ...doc,
    matchingType: doc.matchingType || 'non-po',
    lineItems: doc.lineItems.map((item, index) => ({
      ...item,
      id: item.id || `line-${doc.id}-${index}`,
      taxCode: item.taxCode || 'I',
      taxAmt: typeof item.taxAmt === 'string' ? parseFloat(item.taxAmt) || 0 : item.taxAmt
    }))
  } as ExtendedInvoiceDocument;
}
export function DocumentManagementScreen() {
  const [selectedDocumentId, setSelectedDocumentId] = useState('1');
  const [topPanelPercentage, setTopPanelPercentage] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditAllMode, setIsEditAllMode] = useState(false);
  const [isViewerDetached, setIsViewerDetached] = useState(false);
  const [validationExpanded, setValidationExpanded] = useState(false);
  const [validationHovered, setValidationHovered] = useState(false);
  const [documentOverrides, setDocumentOverrides] = useState<Record<string, Partial<InvoiceDocument>>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const dragStartPercentage = useRef(0);
  const detachedWindowRef = useRef<Window | null>(null);
  const validationRef = useRef<HTMLDivElement>(null);
  // Get document data with any overrides applied
  const baseDocumentData: InvoiceDocument = mockDocuments[selectedDocumentId] || mockDocuments['1'];
  const documentData: InvoiceDocument = {
    ...baseDocumentData,
    ...documentOverrides[selectedDocumentId],
    lineItems: documentOverrides[selectedDocumentId]?.lineItems || baseDocumentData.lineItems
  };
  const adaptedDocument = useMemo(() => adaptDocument(documentData), [documentData]);
  const documentDetails = useMemo(() => getDocumentDetails(documentData), [documentData]);
  // Validate document whenever it changes
  const validation = useMemo(() => {
    return validateInvoice(documentData);
  }, [documentData]);
  const totalErrorCount = validation.errors.length;
  const totalWarningCount = validation.warnings.length;
  const hasValidationIssues = totalErrorCount > 0 || totalWarningCount > 0;
  // Reset validation expanded state when switching documents
  useEffect(() => {
    setValidationExpanded(false);
    setValidationHovered(false);
  }, [selectedDocumentId]);
  // Close validation dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (validationRef.current && !validationRef.current.contains(event.target as Node)) {
        setValidationExpanded(false);
        setValidationHovered(false);
      }
    };
    if (validationExpanded || validationHovered) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [validationExpanded, validationHovered]);
  // Check if viewer is already open on mount
  useEffect(() => {
    const viewerOpen = localStorage.getItem('detached_viewer_open') === 'true';
    setIsViewerDetached(viewerOpen);
  }, []);
  // Store current document ID for DetachedViewer sync
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_DOCUMENT_ID, selectedDocumentId);
    if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
      detachedWindowRef.current.postMessage({
        type: 'DOCUMENT_CHANGED',
        documentId: selectedDocumentId
      }, '*');
    }
  }, [selectedDocumentId]);
  // Listen for detached viewer ready message
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'VIEWER_READY') {
        if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
          detachedWindowRef.current.postMessage({
            type: 'DOCUMENT_CHANGED',
            documentId: selectedDocumentId
          }, '*');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [selectedDocumentId]);
  // Handle document header updates
  const handleUpdateDocument = useCallback((updates: Partial<InvoiceDocument>) => {
    setDocumentOverrides(prev => ({
      ...prev,
      [selectedDocumentId]: {
        ...prev[selectedDocumentId],
        ...updates
      }
    }));
  }, [selectedDocumentId]);
  // Handle line item updates from LineItemsPanel
  const handleUpdateLineItem = useCallback((index: number, updatedItem: ExtendedLineItem) => {
    const currentLineItems = [...documentData.lineItems];
    if (index >= currentLineItems.length) {
      currentLineItems.push(updatedItem as any);
    } else {
      currentLineItems[index] = updatedItem as any;
    }
    const subtotal = currentLineItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const taxAmt = currentLineItems.reduce((sum, item) => {
      const tax = typeof item.taxAmt === 'number' ? item.taxAmt : parseFloat(String(item.taxAmt)) || 0;
      return sum + tax;
    }, 0);
    const total = subtotal + taxAmt;
    setDocumentOverrides(prev => ({
      ...prev,
      [selectedDocumentId]: {
        ...prev[selectedDocumentId],
        lineItems: currentLineItems,
        subtotal,
        taxAmt,
        total
      }
    }));
  }, [documentData.lineItems, selectedDocumentId]);
  // Handle detach viewer button click
  const handleDetachViewer = useCallback(() => {
    if (isViewerDetached && detachedWindowRef.current && !detachedWindowRef.current.closed) {
      detachedWindowRef.current.focus();
    } else {
      localStorage.setItem(STORAGE_KEY_DOCUMENT_ID, selectedDocumentId);
      localStorage.setItem('detached_viewer_open', 'true');
      const baseUrl = window.location.origin + window.location.pathname;
      const viewerWindow = window.open(baseUrl + '#detached-viewer', 'DocumentViewer', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no');
      if (viewerWindow) {
        detachedWindowRef.current = viewerWindow;
        setIsViewerDetached(true);
        const checkClosed = setInterval(() => {
          if (viewerWindow.closed) {
            clearInterval(checkClosed);
            setIsViewerDetached(false);
            localStorage.removeItem('detached_viewer_open');
            detachedWindowRef.current = null;
          }
        }, 1000);
      }
    }
  }, [isViewerDetached, selectedDocumentId]);
  // Handle attach viewer button click
  const handleAttachViewer = useCallback(() => {
    if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
      detachedWindowRef.current.close();
    }
    setIsViewerDetached(false);
    localStorage.removeItem('detached_viewer_open');
    detachedWindowRef.current = null;
  }, []);
  // Toggle edit all mode
  const handleToggleEditAll = useCallback(() => {
    setIsEditAllMode(prev => !prev);
  }, []);
  const handleSaveAll = useCallback(() => {
    setIsEditAllMode(false);
  }, []);
  const handleCancelAll = useCallback(() => {
    setIsEditAllMode(false);
  }, []);
  // Resize drag
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartPercentage.current = topPanelPercentage;
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const h = containerRef.current.clientHeight;
      const delta = (e.clientY - dragStartY.current) / h * 100;
      setTopPanelPercentage(Math.max(20, Math.min(80, dragStartPercentage.current + delta)));
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);
  return <div className="flex bg-gray-50" style={{
    height: 'calc(100vh - 200px)',
    minHeight: '600px'
  }} data-id="element-5016">
      {/* Left Sidebar */}
      <DocumentSidebar documents={mockInvoices} selectedDocumentId={selectedDocumentId} onSelectDocument={setSelectedDocumentId} data-id="element-5017" />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-5018">
        {/* Top Action Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-2 flex-shrink-0" data-id="element-5019">
          {/* Left Side - Validation Status */}
          <div className="flex items-center gap-3" data-id="element-5020">
            {hasValidationIssues ? <div ref={validationRef} className="relative" onMouseEnter={() => {
            if (!validationExpanded) setValidationHovered(true);
          }} onMouseLeave={() => {
            setValidationHovered(false);
          }} data-id="element-5021">
                <button onClick={() => {
              setValidationExpanded(!validationExpanded);
              setValidationHovered(false);
            }} className={`flex items-center gap-2 px-3 py-1.5 border rounded-full transition-colors ${totalErrorCount > 0 ? 'bg-red-50 border-red-300 hover:bg-red-100' : 'bg-amber-50 border-amber-300 hover:bg-amber-100'}`} data-id="element-5022">
                  <AlertCircleIcon className={`w-4 h-4 ${totalErrorCount > 0 ? 'text-red-600' : 'text-amber-600'}`} data-id="element-5023" />
                  <span className={`text-sm font-semibold ${totalErrorCount > 0 ? 'text-red-700' : 'text-amber-700'}`} data-id="element-5024">
                    {totalErrorCount > 0 ? `${totalErrorCount} Error${totalErrorCount !== 1 ? 's' : ''}` : `${totalWarningCount} Warning${totalWarningCount !== 1 ? 's' : ''}`}
                  </span>
                  {validationExpanded ? <ChevronUpIcon className={`w-4 h-4 ${totalErrorCount > 0 ? 'text-red-600' : 'text-amber-600'}`} data-id="element-5025" /> : <ChevronDownIcon className={`w-4 h-4 ${totalErrorCount > 0 ? 'text-red-600' : 'text-amber-600'}`} data-id="element-5026" />}
                </button>

                {/* Dropdown Panel */}
                {(validationExpanded || validationHovered) && <div className="absolute top-full left-0 mt-2 w-[420px] bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[480px] overflow-y-auto" data-id="element-5027">
                    <div className="p-4 space-y-4" data-id="element-5028">
                      {validation.errors.length > 0 && <div data-id="element-5029">
                          <h4 className="text-sm font-semibold text-red-900 mb-3 flex items-center gap-2" data-id="element-5030">
                            <AlertCircleIcon className="w-4 h-4" data-id="element-5031" />
                            Errors ({validation.errors.length})
                          </h4>
                          <div className="space-y-2" data-id="element-5032">
                            {validation.errors.map((error, index) => <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-5033">
                                <div className="text-xs font-bold text-red-900 mb-1" data-id="element-5034">
                                  {error.rule || 'Validation Error'}
                                </div>
                                <div className="text-xs text-red-800 leading-relaxed" data-id="element-5035">
                                  {error.message}
                                </div>
                                {error.field && <div className="text-xs text-red-600 mt-1.5 font-medium" data-id="element-5036">
                                    Field: {error.field}
                                  </div>}
                              </div>)}
                          </div>
                        </div>}
                      {validation.warnings.length > 0 && <div data-id="element-5037">
                          <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2" data-id="element-5038">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-5039">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-5040" />
                            </svg>
                            Warnings ({validation.warnings.length})
                          </h4>
                          <div className="space-y-2" data-id="element-5041">
                            {validation.warnings.map((warning, index) => <div key={index} className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-id="element-5042">
                                <div className="text-xs font-bold text-amber-900 mb-1" data-id="element-5043">
                                  {warning.rule || 'Warning'}
                                </div>
                                <div className="text-xs text-amber-800 leading-relaxed" data-id="element-5044">
                                  {warning.message}
                                </div>
                                {warning.field && <div className="text-xs text-amber-600 mt-1.5 font-medium" data-id="element-5045">
                                    Field: {warning.field}
                                  </div>}
                              </div>)}
                          </div>
                        </div>}
                      <div className="pt-2 border-t border-gray-200" data-id="element-5046">
                        <p className="text-xs text-gray-500" data-id="element-5047">
                          {validationExpanded ? 'Click badge to close' : 'Click to keep open • Hover to preview'}
                        </p>
                      </div>
                    </div>
                  </div>}
              </div> : <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full" data-id="element-5048">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-5049">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-5050" />
                </svg>
                <span className="text-sm font-medium text-green-700" data-id="element-5051">
                  Validation Passed
                </span>
              </div>}
          </div>

          {/* Spacer */}
          <div className="flex-1" data-id="element-5052" />

          {/* Help Button */}
          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" title="Keyboard shortcuts (Shift+?)" data-id="element-5053">
            <span className="text-lg font-semibold" data-id="element-5054">?</span>
          </button>

          {/* Divider */}
          <div className="w-px h-10 bg-gray-200 mx-1" data-id="element-5055" />

          {/* Primary Actions - Stacked icon + text */}
          <button className={`flex flex-col items-center justify-center px-5 py-1.5 rounded-md text-white min-w-[70px] transition-colors ${!validation.isValid ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} disabled={!validation.isValid} title={!validation.isValid ? 'Fix validation errors before submitting' : 'Submit document'} data-id="element-5056">
            <SendIcon className="w-4 h-4 mb-0.5" data-id="element-5057" />
            <span className="text-xs font-medium" data-id="element-5058">Submit</span>
          </button>

          <button className="flex flex-col items-center justify-center px-5 py-1.5 rounded-md text-white bg-red-600 hover:bg-red-700 min-w-[70px] transition-colors" data-id="element-5059">
            <XCircleIcon className="w-4 h-4 mb-0.5" data-id="element-5060" />
            <span className="text-xs font-medium" data-id="element-5061">Reject</span>
          </button>

          <button className="flex flex-col items-center justify-center px-5 py-1.5 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200 min-w-[70px] transition-colors" data-id="element-5062">
            <SaveIcon className="w-4 h-4 mb-0.5" data-id="element-5063" />
            <span className="text-xs font-medium" data-id="element-5064">Save Draft</span>
          </button>

          {/* Divider */}
          <div className="w-px h-10 bg-gray-200 mx-1" data-id="element-5065" />

          {/* Secondary Actions - Ghost style, stacked icon + text */}
          <button className="flex flex-col items-center justify-center px-3 py-1.5 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 min-w-[60px] transition-colors" data-id="element-5066">
            <RefreshCwIcon className="w-4 h-4 mb-0.5" data-id="element-5067" />
            <span className="text-xs font-medium" data-id="element-5068">Reassign</span>
          </button>

          {isViewerDetached ? <button onClick={handleAttachViewer} className="flex flex-col items-center justify-center px-3 py-1.5 rounded-md text-blue-600 hover:text-blue-800 hover:bg-blue-50 min-w-[60px] transition-colors" title="Re-attach document viewer to this window" data-id="element-5069">
              <EyeIcon className="w-4 h-4 mb-0.5" data-id="element-5070" />
              <span className="text-xs font-medium" data-id="element-5071">Attach Viewer</span>
            </button> : <button onClick={handleDetachViewer} className="flex flex-col items-center justify-center px-3 py-1.5 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 min-w-[60px] transition-colors" title="Open document viewer in a separate window" data-id="element-5072">
              <EyeOffIcon className="w-4 h-4 mb-0.5" data-id="element-5073" />
              <span className="text-xs font-medium whitespace-nowrap" data-id="element-5074">
                Detach Viewer
              </span>
            </button>}

          <button onClick={isEditAllMode ? handleSaveAll : handleToggleEditAll} className="flex flex-col items-center justify-center px-3 py-1.5 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 min-w-[60px] transition-colors" data-id="element-5075">
            <Edit3Icon className="w-4 h-4 mb-0.5" data-id="element-5076" />
            <span className="text-xs font-medium" data-id="element-5077">
              {isEditAllMode ? 'Save All' : 'Edit All'}
            </span>
          </button>

          <button onClick={isEditAllMode ? handleCancelAll : undefined} className="flex flex-col items-center justify-center px-3 py-1.5 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 min-w-[60px] transition-colors" data-id="element-5078">
            <XIcon className="w-4 h-4 mb-0.5" data-id="element-5079" />
            <span className="text-xs font-medium" data-id="element-5080">Cancel</span>
          </button>
        </div>

        {/* Resizable Content */}
        <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden" data-id="element-5081">
          {/* Top: Data Panel + Image Viewer (or just Data Panel when detached) */}
          <div className="flex-shrink-0 overflow-hidden px-4 pt-4" style={{
          height: `${topPanelPercentage}%`
        }} data-id="element-5082">
            <div className={`${isViewerDetached ? '' : 'grid grid-cols-2'} gap-4 h-full`} data-id="element-5083">
              <div className="h-full overflow-hidden" data-id="element-5084">
                <DocumentDataPanel details={documentDetails} document={documentData} isEditAllMode={isEditAllMode} onUpdateDocument={handleUpdateDocument} data-id="element-5085" />
              </div>
              {!isViewerDetached && <div className="h-full overflow-hidden" data-id="element-5086">
                  <ImageViewerPanel document={adaptedDocument} data-id="element-5087" />
                </div>}
            </div>
          </div>

          {/* Resize Handle */}
          <div className="flex-shrink-0 px-4 py-2 cursor-ns-resize group" onMouseDown={handleMouseDown} data-id="element-5088">
            <div className={`h-1 rounded-full transition-colors ${isDragging ? 'bg-blue-500' : 'bg-gray-300 group-hover:bg-blue-400'}`} data-id="element-5089">
              <div className="flex items-center justify-center -mt-2" data-id="element-5090">
                <div className={`px-3 py-1 rounded-full transition-all ${isDragging ? 'bg-blue-500 shadow-lg' : 'bg-gray-300 group-hover:bg-blue-400 opacity-0 group-hover:opacity-100'}`} data-id="element-5091">
                  <GripHorizontalIcon className="w-4 h-4 text-white" data-id="element-5092" />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: Line Items Panel */}
          <div className="flex-1 px-4 pb-4 min-h-0 overflow-hidden" data-id="element-5093">
            <div className="h-full overflow-y-auto" data-id="element-5094">
              <LineItemsPanel document={adaptedDocument} onUpdateLineItem={handleUpdateLineItem} isEditAllMode={isEditAllMode} data-id="element-5095" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}